import React, { useContext, useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { OrganisationSettings } from '../../../../interfaces/delivery-company/orders';
import { InvoiceFrequencyPeriodType, InvoiceFrequencyPeriodTypeLabels } from '../../../../enums/extra-charges';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import axios from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { useSnackBar } from '../../../../contexts/snackbar';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import MoneyInput from '../../../common/money-input/money-input';
import { Money } from '../../../../utils/money';
import Decimal from 'decimal.js';

interface InvoiceFrequencyConfigProps {
  settings: OrganisationSettings;
}

function InvoiceFrequencyConfig({ settings }: InvoiceFrequencyConfigProps) {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const { setSettings } = useContext(OrgSettingsContext);

  const [enabled, setEnabled] = useState(settings.invoice_frequency_enabled || false);
  const [periodType, setPeriodType] = useState(settings.invoice_frequency_period_type || InvoiceFrequencyPeriodType.WEEK);
  const [limit, setLimit] = useState<string>(settings.invoice_frequency_limit?.toString() || '');


  const [chargeAmount, setChargeAmount] = useState<Money[]>(settings.invoice_frequency_charge_amount ?? []);
  
  const [isSaving, setIsSaving] = useState(false);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState<{
    limit?: string;
    chargeAmount?: string;
  }>({});
  const [hasChanges, setHasChanges] = useState(false);

  const validateForm = useCallback(() => {
    const errors: { limit?: string; chargeAmount?: string } = {};
    
    if (enabled) {
      // Validate limit
      if (!limit || limit.trim() === '') {
        errors.limit = 'Invoice limit is required when enabled';
      } else if (isNaN(Number(limit)) || Number(limit) < 0) {
        errors.limit = 'Limit must be a non-negative number';
      }

      // Validate charge amount
      if (!chargeAmount || chargeAmount.length === 0) {
        errors.chargeAmount = 'Charge amount is required when enabled';
      } else {
        try {
          const hasInvalidAmount = chargeAmount.some(([, amount]) => {
            // Handle different possible data types from backend
            if (amount && typeof amount === 'object' && 'lessThanOrEqualTo' in amount) {
              // It's a proper Decimal object
              return amount.lessThanOrEqualTo(0);
            } else {
              // It's a string or number, convert to number for comparison
              const numAmount = Number(amount);
              return isNaN(numAmount) || numAmount <= 0;
            }
          });
          if (hasInvalidAmount) {
            errors.chargeAmount = 'Charge amount must be positive';
          }
        } catch (error) {
          console.error('Error validating charge amount:', error);
          errors.chargeAmount = 'Invalid charge amount format';
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [enabled, limit, chargeAmount]);

  // Track changes and validate in real-time
  useEffect(() => {
    const originalEnabled = settings.invoice_frequency_enabled || false;
    const originalPeriodType = settings.invoice_frequency_period_type || InvoiceFrequencyPeriodType.WEEK;
    const originalLimit = settings.invoice_frequency_limit?.toString() || '';
    const originalChargeAmount = JSON.stringify(settings.invoice_frequency_charge_amount || []);

    const changed = 
      enabled !== originalEnabled ||
      periodType !== originalPeriodType ||
      limit !== originalLimit ||
      JSON.stringify(chargeAmount) !== originalChargeAmount;

    setHasChanges(changed);
    
    // Validate in real-time
    validateForm();
  }, [enabled, periodType, limit, chargeAmount, settings, validateForm]);

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/api/v1/settings`,
        {
          invoice_frequency_enabled: enabled,
          invoice_frequency_period_type: enabled ? periodType : null,
          invoice_frequency_limit: enabled ? parseInt(limit) : null,
          invoice_frequency_charge_amount: enabled ? chargeAmount : null,
        },
        config
      );
      if (response.data) {
        setSettings(response.data);
      }
      showMessage('Invoice frequency settings saved successfully', 'success');
      setValidationErrors({});
    } catch (error) {
      console.error('Failed to save invoice frequency settings:', error);
      showMessage('Failed to save settings', 'error');
    }
    setIsSaving(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Configure automatic extra charges for merchants who exceed invoice frequency limits.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        }
        label="Enable Invoice Frequency Charges"
      />

      {enabled && (
        <>
          <Alert severity="info" sx={{ mb: 1 }}>
            Configure the rules for when extra charges should be applied to merchant invoices.
          </Alert>

          <FormControl fullWidth required>
            <InputLabel>Period Type *</InputLabel>
            <Select
              value={periodType}
              label="Period Type *"
              onChange={(e) => setPeriodType(e.target.value as InvoiceFrequencyPeriodType)}
            >
              {Object.entries(InvoiceFrequencyPeriodTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            required
            type="number"
            label="Invoice Limit (per period)"
            value={limit}
            onChange={(e) => {
              setLimit(e.target.value);
            }}
            inputProps={{ min: 0,  }}
            autoComplete='off'
            helperText={"Maximum number of invoices allowed without extra charge"}
          />

          <Box>
            <MoneyInput
              label="Amount to charge for each invoice exceeding the limit"
              money={chargeAmount}
              setMoney={(newAmount) => {
                setChargeAmount(newAmount);
                // Clear validation error when user starts typing
                if (validationErrors.chargeAmount) {
                  setValidationErrors(prev => ({ ...prev, chargeAmount: undefined }));
                }
              }}
            />
            {validationErrors.chargeAmount && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {validationErrors.chargeAmount}
              </Typography>
            )}
          </Box>
        </>
      )}
        
      <Box width={"100%"} textAlign={"center"}>
        {isSaving ? (
          <CircularProgress />
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              isSaving || 
              !hasChanges || 
              Object.keys(validationErrors).length > 0
            }
          >
            Save Settings
          </Button>
        )}
      </Box>
      
    </Box>
  );
}

export default InvoiceFrequencyConfig;

